import { useEffect, useState } from "react";
import { ProfileTrans, DaysOfTheWeekTrans } from "../../utils/translations";
import { updateUser } from "../../services/userService";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * Card for selecting regular days (auto-booking). Reused on Profile and Schedule.
 * Props: user, token, language, setUser, setToaster, className (optional), centerTitle (optional, for Schedule)
 */
export default function RegularDaysCard({ user, token, language, setUser, setToaster, className = "", centerTitle = false }) {
  const [savingRegularDays, setSavingRegularDays] = useState(false);
  const [localRegularDays, setLocalRegularDays] = useState([]);
  const [regularDaysDirty, setRegularDaysDirty] = useState(false);

  const daysTrans = DaysOfTheWeekTrans[language];

  useEffect(() => {
    if (!regularDaysDirty) {
      setLocalRegularDays(Array.isArray(user?.regularDays) ? [...user.regularDays] : []);
    }
  }, [user?.regularDays, regularDaysDirty]);

  const handleDayToggle = (dayNum) => {
    const checked = localRegularDays.includes(dayNum);
    const next = checked
      ? localRegularDays.filter((d) => d !== dayNum)
      : [...localRegularDays, dayNum].sort((a, b) => a - b);
    setLocalRegularDays(next);
    setRegularDaysDirty(true);
  };

  const handleSave = async () => {
    setSavingRegularDays(true);
    try {
      const updated = await updateUser(token, { regularDays: localRegularDays });
      setUser(updated);
      setRegularDaysDirty(false);
      setToaster({
        message: language === "russian" ? "Сохранено" : "Saved",
        type: "success",
        show: true,
      });
    } catch (err) {
      console.error(err);
      setToaster({
        message: language === "russian" ? "Ошибка сохранения" : "Failed to save",
        type: "error",
        show: true,
      });
    } finally {
      setSavingRegularDays(false);
    }
  };

  return (
    <div className={`mb-6 sm:mb-8 bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-gray-100 ${className}`.trim()}>
      <h3 className={`text-lg font-semibold text-gray-800 mb-1 ${centerTitle ? "text-center" : ""}`}>{ProfileTrans[language].regularDaysLabel}</h3>
      <p className="text-xs text-gray-600 mb-4">{ProfileTrans[language].regularDaysHint}</p>
      <div className="flex flex-wrap justify-between gap-1 sm:grid sm:grid-cols-7 sm:gap-4 mb-4">
        {DAY_KEYS.map((key, index) => {
          const dayNum = index;
          const checked = localRegularDays.includes(dayNum);
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleDayToggle(dayNum)}
              disabled={savingRegularDays}
              className={`
                py-2 px-2 rounded-xl text-sm font-semibold transition-all duration-200 touch-manipulation
                focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2
                ${checked
                  ? "bg-amber-600 text-white shadow-md shadow-amber-200/50 hover:bg-amber-700 active:scale-[0.98]"
                  : "bg-amber-50/80 text-amber-800 border-2 border-amber-200 hover:border-amber-300 hover:bg-amber-100/80 active:scale-[0.98]"
                }
              `}
            >
              {daysTrans[key]}
            </button>
          );
        })}
      </div>
      {regularDaysDirty && (
        <button
          type="button"
          onClick={handleSave}
          disabled={savingRegularDays}
          className="w-full sm:w-auto min-w-[120px] py-2 px-3 rounded-xl bg-amber-600 text-white shadow-md hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {savingRegularDays ? (language === "russian" ? "Сохранение…" : "Saving…") : ProfileTrans[language].save}
        </button>
      )}
    </div>
  );
}
